import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Form, 
  Input, 
  DatePicker, 
  InputNumber,
  Modal, 
  Table, 
  Tag, 
  Space, 
  Typography,
  message,
  Tooltip,
  Dropdown
} from 'antd';
import { 
  PlayCircleOutlined, 
  BarChartOutlined,
  EyeOutlined, 
  DownloadOutlined, 
  ReloadOutlined,
  RedoOutlined,
  ExclamationCircleOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  DownOutlined,
  LoadingOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  FilterOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from '../../utils/axios';
import { useAuthCheck } from '../../hooks/useAuth';
import PropertyKeyAutoComplete from './PropertyKeyAutoComplete';
import './analysis.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const isJobRunning = (status) => {
  const runningStatuses = [
    'pending',
    'gathering_insights', 
    'aggregating', 
    'calculating_impacts', 
    'generating_recommendations'
  ];
  return runningStatuses.includes(status);
};

const AggregationJobsPage = () => {
  // Check authentication before rendering
  useAuthCheck();
  
  // Navigation hooks
  const navigate = useNavigate();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [form] = Form.useForm();
  // Polling interval ref
  const pollingRef = useRef(null);
  // Multi-select state for jobs table
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  // Admin check state
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkUserAuth = async () => {
      try {
        const response = await axios.get('/iam/me');
        const userData = response.data;
        const hasAdminRole = userData.role === 'admin';
        const isAdminTenant = userData.tenant_code === 'admin';
        setIsAdmin(hasAdminRole || isAdminTenant);
      } catch (err) {
        setIsAdmin(false);
      }
    };
    checkUserAuth();
  }, []);

  useEffect(() => {
    loadJobs();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const schedulePolling = (shouldPoll) => {
    if (shouldPoll) {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(() => {
          loadJobs();
        }, 5000);
      }
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const loadJobs = async () => {
    setJobsLoading(true);
    try {
      const response = await axios.get('/analyze/aggregation/jobs');
      const jobsList = response.data;
      setJobs(jobsList);
      // Start/stop polling based on active jobs
      const hasActive = jobsList.some(j => isJobRunning(j.status));
      schedulePolling(hasActive);
    } catch (error) {
      if (error.response?.status === 404) {
        message.warning('No aggregation jobs found');
      } else {
        message.error('Failed to load aggregation jobs');
        console.error('Error loading jobs:', error);
      }
    } finally {
      setJobsLoading(false);
    }
  };

  const createAggregationJob = async (values) => {
    setLoading(true);
    try {
      const contactFilter = {};
      if (values.contact_filter_entries?.length) {
        for (const entry of values.contact_filter_entries) {
          if (entry?.key?.trim() && entry?.value?.trim()) {
            contactFilter[entry.key.trim()] = entry.value.trim();
          }
        }
      }

      const requestData = {
        name: values.name || undefined,
        description: values.description,
        filter: {
          start_date: values.dateRange?.[0]?.toISOString(),
          end_date: values.dateRange?.[1]?.toISOString(),
          contacts_threshold: values.contacts_threshold,
          contact_filter: Object.keys(contactFilter).length > 0 ? contactFilter : undefined,
        },
        recalculate_insights: values.recalculate_insights,
        version: values.version
      };

      await axios.post('/analyze/aggregation/jobs', requestData);
      message.success('Aggregation job created and started successfully');
      setCreateModalVisible(false);
      form.resetFields();
      loadJobs();
    } catch (error) {
      message.error('Failed to create aggregation job');
      console.error('Error creating job:', error);
    } finally {
      setLoading(false);
    }
  };

  const restartJob = (jobId, jobName) => {
    Modal.confirm({
      title: 'Restart Aggregation Job',
      content: `Are you sure you want to restart "${jobName}"? This will clear all previous results and checkpoints, and start the analysis from the beginning.`,
      okText: 'Yes, Restart',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await axios.post(`/analyze/aggregation/jobs/${jobId}/restart`);
          message.success('Job restarted successfully');
          loadJobs();
        } catch (error) {
          message.error('Failed to restart job');
          console.error('Error restarting job:', error);
        }
      }
    });
  };

  // Batch helpers (fail-fast)
  const runBatch = async (label, ids, fn) => {
    setBatchLoading(true);
    try {
      for (const id of ids) {
        await fn(id);
      }
      message.success(`${label} completed for ${ids.length} job(s)`);
      setSelectedRowKeys([]);
      loadJobs();
    } catch (e) {
      console.error(`Batch ${label} failed:`, e);
      message.error(`Batch ${label} failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBatchLoading(false);
    }
  };

  const batchRestart = () => {
    Modal.confirm({
      title: 'Restart Selected Jobs',
      content: `Restart ${selectedRowKeys.length} job(s)? This will clear all previous results and checkpoints, and start the analysis from the beginning.`,
      okText: 'Yes, Restart',
      cancelText: 'No',
      onOk: async () => {
        await runBatch('Restart', selectedRowKeys, async (id) => axios.post(`/analyze/aggregation/jobs/${id}/restart`));
      }
    });
  };

  const viewResults = (job) => {
    // Navigate to the analysis page with the job ID parameter
    navigate(`/analysis?jobId=${job.id}`);
  };

  const exportResults = async (jobId, format = 'json') => {
    try {
      const response = await axios.get(`/analyze/aggregation/jobs/${jobId}/results`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `aggregation-results-${jobId}.${format}`;
      link.click();
      URL.revokeObjectURL(url);
      message.success('Results exported successfully');
    } catch (error) {
      message.error('Failed to export results');
      console.error('Error exporting results:', error);
    }
  };

  const getStatusIcon = (status) => {
    const iconStyle = { fontSize: '16px', marginRight: '8px' };
    const statusIcons = {
      pending: <ClockCircleOutlined style={{ ...iconStyle, color: '#faad14' }} />,
      gathering_insights: <LoadingOutlined spin style={{ ...iconStyle, color: '#1890ff' }} />,
      aggregating: <LoadingOutlined spin style={{ ...iconStyle, color: '#1890ff' }} />,
      calculating_impacts: <LoadingOutlined spin style={{ ...iconStyle, color: '#722ed1' }} />,
      generating_recommendations: <LoadingOutlined spin style={{ ...iconStyle, color: '#13c2c2' }} />,
      completed: <CheckCircleOutlined style={{ ...iconStyle, color: '#52c41a' }} />,
      failed: <CloseCircleOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />,
      cancelled: <MinusCircleOutlined style={{ ...iconStyle, color: '#8c8c8c' }} />
    };
    return statusIcons[status] || <ClockCircleOutlined style={iconStyle} />;
  };

  const getProgressText = (job) => {
    if (job.progress_percentage > 0) {
      return `${Math.round(job.progress_percentage)}% - ${job.current_step}`;
    }
    return job.current_step || 'Initializing...';
  };

  const getCheckpointProgress = (job) => {
    const checkpoints = [
      { key: 'insights_gathered', label: 'Insights Gathered', icon: <DatabaseOutlined /> },
      { key: 'insights_aggregated', label: 'Insights Aggregated', icon: <SettingOutlined /> },
      { key: 'impacts_calculated', label: 'Impacts Calculated', icon: <ExclamationCircleOutlined /> },
      { key: 'recommendations_generated', label: 'Recommendations Generated', icon: <CheckCircleOutlined /> }
    ];

    // Find the latest completed checkpoint (last one that is true)
    const latestCheckpoint = [...checkpoints].reverse().find(cp => job[cp.key]);

    if (!latestCheckpoint) {
      return null;
    }

    return (
      <div style={{ marginTop: '4px', marginLeft: '24px' }}>
        <Tag 
          color="green"
          style={{ fontSize: '10px', margin: 0 }}
        >
          {latestCheckpoint.icon} {latestCheckpoint.label}
        </Tag>
      </div>
    );
  };

  const jobsTableColumns = [
    {
      title: 'Job',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={record.status.replace('_', ' ')}>
              {getStatusIcon(record.status)}
            </Tooltip>
            <span style={{ fontWeight: 'bold' }}>{text}</span>
          </div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#666', marginLeft: '24px' }}>{record.description}</div>
          )}
          {isJobRunning(record.status) && (
            <div style={{ fontSize: '12px', marginTop: '4px', marginLeft: '24px', color: '#666' }}>
              {getProgressText(record)}
            </div>
          )}
          {record.status === 'failed' && record.error_message && (
            <div 
              className="error-details"
              style={{ fontSize: '12px', marginTop: '4px', marginLeft: '24px', color: '#ff4d4f' }}
              title={record.error_message}
            >
              {record.error_message.length > 50 
                ? `${record.error_message.substring(0, 50)}...` 
                : record.error_message}
            </div>
          )}
          {isJobRunning(record.status) && getCheckpointProgress(record)}
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, record) => {
        if (record.status !== 'completed' && record.started_at) {
          const duration = dayjs().diff(dayjs(record.started_at), 'minutes');
          return `${duration}m (running)`;
        } else if (record.completed_at && record.started_at) {
          const duration = dayjs(record.completed_at).diff(dayjs(record.started_at), 'minutes');
          return `${duration}m`;
        }
        return '-';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => {
        const canRestart = !isJobRunning(record.status);

        const restartMenuItems = [
          {
            key: 'restart',
            label: 'Restart Job',
            icon: <RedoOutlined />,
          }
        ];

        const handleMenuClick = ({ key }) => {
          if (key === 'restart') {
            restartJob(record.id, record.name);
          }
        };

        return (
          <Space size="small">
            <Tooltip title="View Results">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => viewResults(record)}
                size="small"
              />
            </Tooltip>
            {record.status === 'completed' && (
              <Tooltip title="Export Results">
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => exportResults(record.id)}
                  size="small"
                />
              </Tooltip>
            )}
            {canRestart && (
              <Dropdown
                menu={{ items: restartMenuItems, onClick: handleMenuClick }}
                trigger={['click']}
                placement="bottomRight"
              >
                <Button
                  type="link"
                  icon={<RedoOutlined />}
                  size="small"
                >
                  <DownOutlined />
                </Button>
              </Dropdown>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="analysis-page">
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2}>Analysis Jobs</Title>
            <Text type="secondary">
              Manage aggregation analysis jobs. Create new analyses, monitor progress, and view results.
            </Text>
          </div>
          <Space>
            <Button
              type="default"
              onClick={() => navigate('/analysis?view=insights')}
              icon={<BarChartOutlined />}
              size="large"
            >
              Insights
            </Button>
            {isAdmin && (
              <Tooltip title="Configure scheduled analysis jobs">
                <Button 
                  type="default"
                  onClick={() => navigate('/analysis?view=scheduler')}
                  icon={<ScheduleOutlined />}
                  size="large"
                >
                  Scheduler <Tag color="orange" style={{ marginLeft: 4, marginRight: 0 }}>Beta</Tag>
                </Button>
              </Tooltip>
            )}
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => setCreateModalVisible(true)}
              size="large"
            >
              New Analysis
            </Button>
          </Space>
        </div>
      </div>

      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Aggregation Jobs</span>
            <Space>
              {selectedRowKeys.length > 0 && (
                <>
                  <Button
                    onClick={batchRestart}
                    icon={<RedoOutlined />}
                    loading={batchLoading}
                    disabled={!selectedRowKeys.some(id => {
                      const job = jobs.find(j => j.id === id);
                      return job && !isJobRunning(job.status);
                    })}
                  >
                    Restart Selected ({selectedRowKeys.length})
                  </Button>
                </>
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={loadJobs}
                loading={jobsLoading}
              >
                Refresh
              </Button>
            </Space>
          </div>
        }
      >
        <Table
          columns={jobsTableColumns}
          dataSource={jobs}
          rowKey="id"
          loading={jobsLoading}
          pagination={false}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            getCheckboxProps: (record) => ({
              disabled: isJobRunning(record.status),
            }),
          }}
        />
      </Card>

      {/* Create Job Modal */}
      <Modal
        title="Create Aggregation Job"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={createAggregationJob}
          initialValues={{
            dateRange: [dayjs().subtract(14, 'days'), dayjs()],
            recalculate_insights: false,
            version: "short",
            contacts_threshold: 10
          }}
        >
          <Form.Item
            name="name"
            label="Job Name (Optional)"
          >
            <Input placeholder="Leave empty to auto-generate name with current date/time" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea 
              placeholder="Optional description of this analysis job"
              rows={2}
            />
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Analysis Period"
            rules={[{ required: true, message: 'Please select date range' }]}
          >
            <RangePicker
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
              placeholder={['Start Date', 'End Date']}
              minDate={dayjs().subtract(30, 'days')}
              maxDate={dayjs()}
            />
          </Form.Item>

          <Form.Item
            name="contacts_threshold"
            label="Contacts Threshold"
            extra="Skip insights that affect less than this number of contacts."
          >
            <InputNumber min={0} style={{ width: 120 }} />
          </Form.Item>

          <Form.Item label={<span><FilterOutlined style={{ marginRight: 4 }} />Contact Filter</span>}>
            <Form.List name="contact_filter_entries">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'key']}
                        rules={[{ required: true, message: 'Property key required' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <PropertyKeyAutoComplete placeholder="Property key (e.g. company)" style={{ width: 200 }} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'value']}
                        rules={[{ required: true, message: 'Match value required' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Match value" style={{ width: 200 }} />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                    </Space>
                  ))}
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Filter
                  </Button>
                </>
              )}
            </Form.List>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
              Only analyze contacts whose properties match these values (substring match).
            </Text>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setCreateModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create & Start Job
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AggregationJobsPage;
